/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Link } from 'js/lib/react-router';
import { useParams } from 'js/lib/useRouter';

import { IconButton } from '@coursera/cds-core';
import { SplitScreenIcon } from '@coursera/cds-icons';

import { useSideNavCollapse } from 'bundles/enterprise-admin-navigation/components/side-nav/base/SideNavCollapseProvider';
import { getHomeLink } from 'bundles/enterprise-admin-navigation/utils/AdminNavigationUtils';

import _t from 'i18n!nls/enterprise-admin-navigation';

const styles = {
  container: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
    border-bottom: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    flex-shrink: 0;
  `,
  containerCollapsed: css`
    justify-content: center;
    padding: var(--cds-spacing-100) var(--cds-spacing-200);
  `,
  logo: css`
    display: flex;
    align-items: center;
    height: 20px;
    padding-left: var(--cds-spacing-150);
    flex: 1;
    min-width: 0;
    transition:
      opacity 200ms ease,
      padding 200ms ease;
    opacity: 1;
    text-decoration: none;
  `,
  logoHidden: css`
    opacity: 0;
    padding-left: 0;
  `,
  toggleButton: css`
    transform: rotate(180deg);
    color: var(--cds-color-neutral-primary-weak);
  `,
};

type Props = {
  logo?: React.ReactNode;
};

/**
 *
 * The SideNavHeader component is used to render the header of the sidebar.
 * @param logo - The logo to render in the header
 * @returns The SideNavHeader component
 */
export const SideNavHeader = ({ logo }: Props) => {
  const { isSideNavCollapsed, toggle } = useSideNavCollapse();
  const params = useParams();

  return (
    <div css={[styles.container, isSideNavCollapsed && styles.containerCollapsed]}>
      <Link
        to={getHomeLink(params)}
        css={[styles.logo, isSideNavCollapsed && styles.logoHidden]}
        data-testid="side-nav-logo"
        aria-label={_t('Go to admin home')}
        aria-hidden={isSideNavCollapsed || undefined}
        tabIndex={isSideNavCollapsed ? -1 : undefined}
      >
        {logo}
      </Link>
      <IconButton
        variant="ghost"
        onClick={toggle}
        tooltip={isSideNavCollapsed ? _t('Expand navigation') : _t('Collapse navigation')}
        aria-expanded={!isSideNavCollapsed}
        aria-label={isSideNavCollapsed ? _t('Expand navigation') : _t('Collapse navigation')}
        css={styles.toggleButton}
        icon={<SplitScreenIcon size="medium" />}
      />
    </div>
  );
};

export default SideNavHeader;
